import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faChartLine, faClockRotateLeft, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { getFirestore } from '@firebase/firestore';
import { app } from '../../firebase.js';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';
import { useNavigate } from 'react-router-dom';
import { loadBusinessInfoSnapshot, formatDateTime, formatRate } from './businessInfoShared';

function ExchangeRateInfo() {
  const db = getFirestore(app);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currencySettings, setCurrencySettings] = useState(null);
  const [exchangeRateHistory, setExchangeRateHistory] = useState([]);

  const loadPage = async () => {
    const snapshot = await loadBusinessInfoSnapshot(db);
    setCurrencySettings(snapshot.currencySettings);
    setExchangeRateHistory(snapshot.exchangeRateHistory);
  };

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadPage();
      } catch (error) {
        console.error('Failed to load exchange rate info:', error);
        setCurrencySettings(null);
        setExchangeRateHistory([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, []);

  const refreshPage = async () => {
    try {
      setRefreshing(true);
      await loadPage();
    } catch (error) {
      console.error('Failed to refresh exchange rate info:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const summary = useMemo(() => ({
    usdToKesRate: Number(currencySettings?.usdToKesRate || 0),
    kesToUsdRate: Number(currencySettings?.kesToUsdRate || 0),
    historyCount: exchangeRateHistory.length
  }), [currencySettings, exchangeRateHistory]);

  const sampleKes = useMemo(() => {
    return Number.isFinite(summary.usdToKesRate) ? summary.usdToKesRate * 1000 : 0;
  }, [summary.usdToKesRate]);

  return (
    <div className="mainContainer">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />
        <div className="clientsComp applicationsPage businessInfoPage">
          <div className="appsHeader">
            <div>
              <p className="appsKicker">Business Info</p>
              <h4>Exchange Rate Info</h4>
              <p className="appsSubtext">
                Current USD to KES settings plus the most recent change history for quick financial reference.
              </p>
            </div>
            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>USD/KES</span>
                <strong>{formatRate(summary.usdToKesRate)}</strong>
              </div>
              <div className="appsStatChip">
                <span>KES/USD</span>
                <strong>{formatRate(summary.kesToUsdRate)}</strong>
              </div>
              <div className="appsStatChip">
                <span>History</span>
                <strong>{summary.historyCount}</strong>
              </div>
            </div>
          </div>

          <div className="appsInsightsCard">
            <div className="d-flex flex-wrap justify-content-between gap-3 align-items-start">
              <div>
                <p className="appsKicker">Current Rate</p>
                <h6 className="mb-1">
                  <FontAwesomeIcon icon={faDollarSign} className="me-2" />
                  1 USD = KES {formatRate(summary.usdToKesRate)}
                </h6>
                <p className="appsSubtext">
                  Used anywhere the app needs to translate USD values into local currency.
                </p>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <span className="cohortStatusBadge cohortStatusBadge--joined">
                  <FontAwesomeIcon icon={faChartLine} className="me-1" />
                  1,000 USD = KES {formatRate(sampleKes, 2)}
                </span>
                <span className="cohortStatusBadge cohortStatusBadge--joined">
                  <FontAwesomeIcon icon={faClockRotateLeft} className="me-1" />
                  Updated {formatDateTime(currencySettings?.updatedAt)}
                </span>
              </div>
            </div>
            {currencySettings?.note && (
              <p className="appsSnapshotAdvice mt-3 mb-0">
                <strong>Note:</strong> {currencySettings.note}
              </p>
            )}
          </div>

          <div className="appsFiltersCard">
            <Button variant="outline-dark" onClick={() => navigate('/settings')}>
              Open Settings
            </Button>
            <Button variant="outline-dark" onClick={refreshPage} disabled={refreshing || loading}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          <div className="appsTableCard">
            <div className="appsTableHeader">
              <h6>Rate History</h6>
              <span>{exchangeRateHistory.length} recent update{exchangeRateHistory.length === 1 ? '' : 's'}</span>
            </div>
            <div className="table-responsive">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status" />
                  <p>Loading exchange rate info...</p>
                </div>
              ) : exchangeRateHistory.length === 0 ? (
                <div className="incomeStateCard incomeEmptyState">
                  <h6>No exchange-rate changes recorded yet</h6>
                  <p>Save a rate from Settings to start tracking the change history here.</p>
                </div>
              ) : (
                <Table hover responsive className="appsTable">
                  <thead>
                    <tr>
                      <th>Updated</th>
                      <th>USD to KES</th>
                      <th>KES to USD</th>
                      <th>Note</th>
                      <th>Updated By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exchangeRateHistory.map((item) => (
                      <tr key={item.id}>
                        <td>{formatDateTime(item.createdAt || item.updatedAt)}</td>
                        <td>{formatRate(item.usdToKesRate)}</td>
                        <td>{formatRate(item.kesToUsdRate)}</td>
                        <td>{item.note || '—'}</td>
                        <td>{item.updatedByEmail || item.updatedBy || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExchangeRateInfo;
